// Packages
import { atom, selector, selectorFamily } from 'recoil'

export const countryId = atom({
  key: 'countryId',
  default: null,
})

export const cityId = atom({
  key: 'cityId',
  default: null,
})

export const modalMerchants = atom({
  key: 'modalMerchants',
  default: [],
})

export const modalMerchant = atom({
  key: 'modalMerchant',
  default: null,
})

export const showMerchantsModal = atom({
  key: 'showMerchantsModal',
  default: false,
})

export const cuisineId = atom({
  key: 'cuisineId',
  default: null,
})

export const filterHalal = atom({
  key: 'filterHalal',
  default: false,
})

export const searchQuery = atom({
  key: 'searchQuery',
  default: '',
})

export const userPostalCode = atom({
  key: 'userPostalCode',
  default: '',
})

export const userCoordinates = atom({
  key: 'userCoordinates',
  default: null,
})

export const sortMethod = atom({
  key: 'sortMethod',
  default: 'alphabetical',
})

export const postalCodeValidationError = atom({
  key: 'postalCodeValidationError',
  default: null,
})

// --- Draft filter state ---------------------------------------------------
// The mobile "Refine your search" sheet edits these draft atoms instead of the
// real ones above, so nothing outside the sheet changes until "Confirm" copies
// draft -> real. "Cancel" simply discards them (the real atoms are untouched).
export const draftCountryId = atom({ key: 'draftCountryId', default: null })
export const draftCityId = atom({ key: 'draftCityId', default: null })
export const draftCuisineId = atom({ key: 'draftCuisineId', default: null })
export const draftFilterHalal = atom({ key: 'draftFilterHalal', default: false })
export const draftSearchQuery = atom({ key: 'draftSearchQuery', default: '' })
export const draftUserPostalCode = atom({
  key: 'draftUserPostalCode',
  default: '',
})
export const draftUserCoordinates = atom({
  key: 'draftUserCoordinates',
  default: null,
})
export const draftSortMethod = atom({
  key: 'draftSortMethod',
  default: 'alphabetical',
})
export const draftPostalCodeValidationError = atom({
  key: 'draftPostalCodeValidationError',
  default: null,
})

export const countryData = selector({
  key: 'countryData',
  get: async () => {
    const res = await fetch(`${process.env.REACT_APP_API_PATH}/api/countries`)
    return await res.json()
  },
})

export const cityData = selector({
  key: 'cityData',
  get: async ({ get }) => {
    const id = get(countryId)

    if (!id) return null

    const res = await fetch(
      `${process.env.REACT_APP_API_PATH}/api/country/${id}/cities`,
    )

    return await res.json()
  },
})

export const cuisineData = selector({
  key: 'cuisineData',
  get: async ({ get }) => {
    const id = get(countryId)

    if (!id) return null

    const res = await fetch(
      `${process.env.REACT_APP_API_PATH}/api/country/${id}/cuisine`,
    )
    return await res.json()
  },
})

// Draft equivalents of cityData/cuisineData, keyed off the draft country so the
// sheet's city/cuisine dropdowns reflect a not-yet-confirmed country change.
export const draftCityData = selector({
  key: 'draftCityData',
  get: async ({ get }) => {
    const id = get(draftCountryId)
    if (!id) return null
    const res = await fetch(
      `${process.env.REACT_APP_API_PATH}/api/country/${id}/cities`,
    )
    return await res.json()
  },
})

export const draftCuisineData = selector({
  key: 'draftCuisineData',
  get: async ({ get }) => {
    const id = get(draftCountryId)
    if (!id) return null
    const res = await fetch(
      `${process.env.REACT_APP_API_PATH}/api/country/${id}/cuisine`,
    )
    return await res.json()
  },
})

export const merchantsData = selectorFamily({
  key: 'merchantsData',
  get:
    (locale) =>
    async ({ get }) => {
      const id = get(countryId)

      if (!id) return null

      // Extract country code from locale
      // Country of origin, always second (after '-')
      const origin = locale.split('-')[1]

      const res = await fetch(
        `${process.env.REACT_APP_API_PATH}/api/country/${id}/merchants?origin=${encodeURIComponent(origin)}`,
      )

      const r = await res.json()

      // Sort merchants by isNew property
      if (locale === 'zh-tw') {
        r.sort((a, b) => {
          if (a.businessData.isNew && !b.businessData.isNew) {
            return -1
          } else if (!a.businessData.isNew && b.businessData.isNew) {
            return 1
          }
          return 0
        })
      }

      // Sort merchants by isNew property
      if (locale === 'zh-tw') {
        r.sort((a, b) => {
          const aIsNew = a.isMerchantGroup
            ? a.allMerchantsNew
            : a.businessData.isNew
          const bIsNew = b.isMerchantGroup
            ? b.allMerchantsNew
            : b.businessData.isNew
          if (aIsNew && !bIsNew) {
            return -1
          } else if (!aIsNew && bIsNew) {
            return 1
          }
          return 0
        })
      }

      return r
    },
})

export const countryProps = selector({
  key: 'countryProps',
  get: ({ get }) => {
    const countries = get(countryData)
    const id = get(countryId)
    let props = null
    countries.forEach((region) => {
      const country = region.countries.find((c) => c.key === id)
      if (country && !props) {
        props = country
      }
    })
    return props || {}
  },
})

export const filters = selector({
  key: 'filters',
  get: ({ get }) => {
    return {
      country: get(countryId),
      city: get(cityId),
      cuisine: get(cuisineId),
    }
  },
})
